<template>
  <div class="card mt-3">
    <h5 class="card-header d-flex justify-content-between align-items-center">
      <span><i class="bi bi-robot"></i> AI Review</span>
      <span class="d-flex align-items-center gap-2">
        <span
          v-if="review && confidencePercent !== null"
          class="badge confidence-badge"
          :class="confidenceClass"
          title="Model confidence"
        >
          {{ confidencePercent }}% confidence
        </span>
        <button
          class="btn btn-sm btn-outline-primary"
          :disabled="loading"
          @click="openPrompt"
        >
          <span v-if="loading" class="spinner-border spinner-border-sm me-1" role="status"></span>
          {{ review ? 'Regenerate' : 'Generate review' }}
        </button>
      </span>
    </h5>
    <div class="card-body">
      <div v-if="error" class="alert alert-danger mb-3">{{ error }}</div>
      <div v-if="loading && !review" class="text-center text-muted">
        <span class="spinner-border spinner-border-sm"></span> Loading...
      </div>
      <template v-else-if="review">
        <p class="ai-review-content mb-3">{{ review.content }}</p>
        <div class="d-flex justify-content-between align-items-center">
          <span v-if="review.approved" class="badge bg-success">
            <i class="bi bi-check-circle"></i> Approved
          </span>
          <span v-else class="badge bg-secondary">Not approved</span>
          <span>
            <button
              v-if="!review.approved"
              class="btn btn-sm btn-success"
              :disabled="loading"
              @click="approve"
            >
              Approve
            </button>
            <button
              v-else
              class="btn btn-sm btn-outline-danger"
              :disabled="loading"
              @click="revoke"
            >
              Revoke
            </button>
          </span>
        </div>
      </template>
      <div v-else class="text-muted">No AI review yet.</div>
    </div>
    <AIPromptModal ref="promptModal" @generate="onGenerate" />
  </div>
</template>

<script>
import AIReviewController from '@/controllers/AIReviewController';
import AIPromptModal from '@/components/AIPromptModal.vue';

// Self-contained teacher island: loads/generates/approves/revokes the AI review of
// a single submission and shows a confidence badge (via AIReview model getters).
export default {
  name: 'AIReviewPanel',
  components: { AIPromptModal },
  props: {
    submissionId: { type: [Number, String], required: true },
  },
  data() {
    return {
      review: null,
      loading: false,
      error: null,
    };
  },
  computed: {
    confidencePercent() {
      return this.review ? this.review.confidencePercent : null;
    },
    confidenceClass() {
      return this.review ? this.review.confidenceClass : 'bg-secondary';
    },
  },
  async created() {
    await this.refresh();
  },
  methods: {
    openPrompt() {
      if (this.$refs.promptModal) this.$refs.promptModal.show();
    },
    async refresh() {
      this.loading = true;
      this.error = null;
      try {
        this.review = await AIReviewController.getBySubmission(this.submissionId);
      } catch (e) {
        this.error = e.message || 'Failed to load AI review';
      } finally {
        this.loading = false;
      }
    },
    async onGenerate(prompt) {
      this.loading = true;
      this.error = null;
      try {
        this.review = await AIReviewController.generate(this.submissionId, prompt);
      } catch (e) {
        this.error = e.message || 'Failed to generate AI review';
      } finally {
        this.loading = false;
      }
    },
    async approve() {
      this.loading = true;
      this.error = null;
      try {
        this.review = await AIReviewController.approve(this.submissionId);
      } catch (e) {
        this.error = e.message || 'Failed to approve AI review';
      } finally {
        this.loading = false;
      }
    },
    async revoke() {
      this.loading = true;
      this.error = null;
      try {
        this.review = await AIReviewController.revoke(this.submissionId);
      } catch (e) {
        this.error = e.message || 'Failed to revoke AI review';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.confidence-badge {
  font-size: 0.75rem;
}
.ai-review-content {
  white-space: pre-wrap;
}
</style>
